import React, { useEffect, useMemo } from 'react';

import { FlexiblePaymentFrequency } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { toCamelCase } from 'Shared/utils';

import {
  PriceCustomizerProvider,
  usePriceCustomizer,
} from '../PriceCustomizerContext';
import { PlanComparison } from '../PlanComparison';
import {
  PaymentPlanComparisonContentProps,
  PaymentPlanComparisonPanelProps,
} from './types';

const PanelContent = ({
  variant,
  isLoading,
  conceptLabels,
  selectedLabel,
  onPlanSelect,
}: PaymentPlanComparisonContentProps) => {
  const {
    frequency,
    selectedPaymentPlan,
    selectFrequency,
  } = usePriceCustomizer();

  const initialSelectedFrequency = useMemo(() => {
    if (!selectedPaymentPlan) {
      return null;
    }

    return toCamelCase<FlexiblePaymentFrequency>(
      selectedPaymentPlan.paymentFrequencyIdentifier
    );
  }, [selectedPaymentPlan]);

  useEffect(() => {
    if (!frequency && initialSelectedFrequency) {
      selectFrequency(initialSelectedFrequency);
    }
  }, [frequency, initialSelectedFrequency, selectFrequency]);

  return (
    <PlanComparison
      isLoading={isLoading}
      onPlanSelect={onPlanSelect}
      selectedLabel={selectedLabel}
      variant={variant}
      conceptLabels={conceptLabels}
    />
  );
};

export const PaymentPlanComparisonPanel = ({
  rate,
  isLoading,
  selectedLabel,
  variant,
  conceptLabels,
  onPlanSelect,
  children,
}: PaymentPlanComparisonPanelProps) => {
  if (!rate) {
    return null;
  }

  return (
    <PriceCustomizerProvider rate={rate}>
      {selectedFrequency => (
        <>
          <PanelContent
            isLoading={isLoading}
            onPlanSelect={onPlanSelect}
            selectedLabel={selectedLabel}
            variant={variant}
            conceptLabels={conceptLabels}
          />
          {children?.(selectedFrequency)}
        </>
      )}
    </PriceCustomizerProvider>
  );
};
